'use client';

import { useState, useEffect } from 'react';
import { platformExpenseApi } from '../../services/platformExpenseApi';
import { vendorApi } from '../../services/vendorApi';
import { Receipt, Plus, Pencil, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

const CATEGORY_LABELS: Record<string, string> = {
    hosting: 'Hosting',
    sms: 'SMS',
    domain: 'Domain',
    marketing: 'Pazarlama',
    other: 'Diğer',
};

export default function PlatformExpenseList() {
    const [expenses, setExpenses] = useState<any[]>([]);
    const [vendors, setVendors] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [filterVendorId, setFilterVendorId] = useState('');

    // Form modal
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [form, setForm] = useState({
        vendorId: '', title: '', category: 'hosting', amount: 0,
        periodStart: '', periodEnd: '', description: '',
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchExpenses();
        fetchVendors();
    }, [filterVendorId]);

    const fetchVendors = async () => {
        try {
            const res = await vendorApi.getAllVendors({ limit: 100 });
            setVendors(res.vendors || []);
        } catch { /* ignore */ }
    };

    const fetchExpenses = async () => {
        try {
            setLoading(true);
            const params: any = {};
            if (filterVendorId) params.vendorId = filterVendorId;
            const res = await platformExpenseApi.getAll(params);
            setExpenses(res.expenses || []);
        } catch {
            toast.error('Giderler yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    const openCreate = () => {
        setEditingId(null);
        setForm({ vendorId: '', title: '', category: 'hosting', amount: 0, periodStart: '', periodEnd: '', description: '' });
        setShowModal(true);
    };

    const openEdit = (expense: any) => {
        setEditingId(expense.id);
        const toDateStr = (val: any) => {
            if (!val) return '';
            const d = val._seconds ? new Date(val._seconds * 1000) : new Date(val);
            return d.toISOString().split('T')[0];
        };
        setForm({
            vendorId: expense.vendorId,
            title: expense.title,
            category: expense.category,
            amount: expense.amount,
            periodStart: toDateStr(expense.periodStart),
            periodEnd: toDateStr(expense.periodEnd),
            description: expense.description || '',
        });
        setShowModal(true);
    };

    const handleSave = async () => {
        if (!form.vendorId || !form.title || !form.amount || !form.periodStart || !form.periodEnd) {
            toast.error('Zorunlu alanları doldurun');
            return;
        }
        try {
            setSaving(true);
            if (editingId) {
                await platformExpenseApi.update(editingId, form);
                toast.success('Gider güncellendi');
            } else {
                await platformExpenseApi.create(form);
                toast.success('Gider eklendi');
            }
            setShowModal(false);
            fetchExpenses();
        } catch (err: any) {
            toast.error(err.response?.data?.error || 'İşlem başarısız');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Bu gideri silmek istediğinize emin misiniz?')) return;
        try {
            await platformExpenseApi.remove(id);
            toast.success('Gider silindi');
            fetchExpenses();
        } catch (err: any) {
            toast.error(err.response?.data?.error || 'Silinemedi');
        }
    };

    const formatCurrency = (val: number) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(val || 0);
    const formatDate = (val: any) => {
        if (!val) return '-';
        const d = val._seconds ? new Date(val._seconds * 1000) : new Date(val);
        return d.toLocaleDateString('tr-TR');
    };

    return (
        <div className="p-6">
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                    <Receipt size={24} /> Platform Giderleri
                </h1>
                <button
                    onClick={openCreate}
                    className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                >
                    <Plus size={18} /> Yeni Gider
                </button>
            </div>

            {/* Filter */}
            <div className="bg-white rounded-lg shadow p-4 mb-6">
                <label className="block text-xs font-medium text-gray-600 mb-1">Vendor Filtre</label>
                <select
                    value={filterVendorId}
                    onChange={(e) => setFilterVendorId(e.target.value)}
                    className="border rounded-lg px-3 py-2 text-sm"
                >
                    <option value="">Tümü</option>
                    {vendors.map((v) => (
                        <option key={v.id} value={v.id}>{v.firstName} {v.lastName}</option>
                    ))}
                </select>
            </div>

            {/* Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                {loading ? (
                    <div className="flex justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                ) : expenses.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">Gider bulunamadı</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-gray-600 font-medium">
                            <tr>
                                <th className="px-4 py-3 text-left">Başlık</th>
                                <th className="px-4 py-3 text-left">Kategori</th>
                                <th className="px-4 py-3 text-left">Vendor</th>
                                <th className="px-4 py-3 text-left">Dönem</th>
                                <th className="px-4 py-3 text-right">Tutar</th>
                                <th className="px-4 py-3 text-center">İşlem</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {expenses.map((exp) => (
                                <tr key={exp.id} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 font-medium">{exp.title}</td>
                                    <td className="px-4 py-3">
                                        <span className="text-xs px-2 py-0.5 rounded bg-gray-100">
                                            {CATEGORY_LABELS[exp.category] || exp.category}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-gray-600">
                                        {vendors.find(v => v.id === exp.vendorId)
                                            ? `${vendors.find(v => v.id === exp.vendorId).firstName} ${vendors.find(v => v.id === exp.vendorId).lastName}`
                                            : exp.vendorId}
                                    </td>
                                    <td className="px-4 py-3 text-gray-600">
                                        {formatDate(exp.periodStart)} - {formatDate(exp.periodEnd)}
                                    </td>
                                    <td className="px-4 py-3 text-right font-medium">{formatCurrency(exp.amount)}</td>
                                    <td className="px-4 py-3 text-center">
                                        <div className="flex gap-2 justify-center">
                                            <button onClick={() => openEdit(exp)} className="text-gray-500 hover:text-blue-600">
                                                <Pencil size={16} />
                                            </button>
                                            <button onClick={() => handleDelete(exp.id)} className="text-gray-500 hover:text-red-600">
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Create/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setShowModal(false)}>
                    <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
                        <h2 className="text-lg font-bold mb-4">{editingId ? 'Gider Düzenle' : 'Yeni Gider Ekle'}</h2>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Vendor</label>
                                <select
                                    value={form.vendorId}
                                    onChange={(e) => setForm({ ...form, vendorId: e.target.value })}
                                    className="w-full border rounded-lg px-3 py-2"
                                    disabled={!!editingId}
                                >
                                    <option value="">Seçin...</option>
                                    {vendors.map((v) => (
                                        <option key={v.id} value={v.id}>{v.firstName} {v.lastName}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Başlık</label>
                                <input
                                    type="text"
                                    value={form.title}
                                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                                    placeholder="ör: Hosting - Mart 2026"
                                    className="w-full border rounded-lg px-3 py-2"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Kategori</label>
                                    <select
                                        value={form.category}
                                        onChange={(e) => setForm({ ...form, category: e.target.value })}
                                        className="w-full border rounded-lg px-3 py-2"
                                    >
                                        {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                                            <option key={k} value={k}>{v}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Tutar (₺)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={form.amount}
                                        onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })}
                                        className="w-full border rounded-lg px-3 py-2"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Dönem Başlangıcı</label>
                                    <input
                                        type="date"
                                        value={form.periodStart}
                                        onChange={(e) => setForm({ ...form, periodStart: e.target.value })}
                                        className="w-full border rounded-lg px-3 py-2"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Dönem Bitişi</label>
                                    <input
                                        type="date"
                                        value={form.periodEnd}
                                        onChange={(e) => setForm({ ...form, periodEnd: e.target.value })}
                                        className="w-full border rounded-lg px-3 py-2"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Açıklama</label>
                                <textarea
                                    value={form.description}
                                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                                    rows={2}
                                    className="w-full border rounded-lg px-3 py-2"
                                    placeholder="Opsiyonel..."
                                />
                            </div>
                        </div>
                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
                            >
                                {saving ? 'Kaydediliyor...' : (editingId ? 'Güncelle' : 'Ekle')}
                            </button>
                            <button
                                onClick={() => setShowModal(false)}
                                className="flex-1 border border-gray-300 py-2 rounded-lg hover:bg-gray-50 transition"
                            >
                                İptal
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
